import * as Sentry from "@sentry/react";
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createSelectors } from './createSelectors';
import { AvailableCustomer, refreshContext, UserContextResponse } from '../apis/authApi';
import { PaymentSettings } from '../apis/paymentSettingApi';
import { useCompanyStore } from './';

interface UserState {
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setCustomerId: (customerId: string) => void;
  setCustomerData: (customerData: UserContextResponse) => void;
  hasRoles: (...requiredRoles: string[]) => boolean;
  email: string | null;
  companyName: string | null;
  companyWebsite: string | null;
  backOfficeRootUrl: string | null;
  setEmail: (email: string) => void;
  availableCustomers: AvailableCustomer[];
  currentCustomer: AvailableCustomer | null;
  roles: string[];
  hints: string[];
  paymentSettings: PaymentSettings | null;
  setPaymentSettings: (paymentSettings: PaymentSettings | null) => void;
  clearState: () => void;
  refreshContext: (customerId?: string) => Promise<void>;
}

const useSessionStoreBase = create<UserState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      setIsAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),
      setCustomerId: (customerId: string) => {
        const currentCustomer = get().availableCustomers.find((x) => x.id === customerId);
        if (currentCustomer) {
          set({ currentCustomer });
        }
      },
      setCustomerData: (customerData: UserContextResponse) => {
        Sentry.setTag("CompanyId", customerData.companyId);
        Sentry.setTag("CustomerId", customerData.customerId);
        Sentry.setUser({
          id: customerData.id,
          email: customerData.email,
          username: customerData.email,
          CustomerId: customerData.customerId,
          CompanyId: customerData.companyId,
        });
        
        const currentCustomer = customerData.availableCustomers.find((x) => x.id === customerData.customerId);
        if (currentCustomer) {
          set({
            availableCustomers: customerData.availableCustomers,
            currentCustomer,
            companyName: customerData.companyName,
            companyWebsite: customerData.companyWebsite,
            backOfficeRootUrl: customerData.backOfficeRootUrl,
            roles: customerData.roles,
            hints: customerData.hints,
          });
        }
      },
      hasRoles: (...requiredRoles: string[]) => {
        const roles = get().roles;
        for (const requiredRole of requiredRoles) {
          if (!roles.includes(requiredRole)) {
            return false;
          }
        }
        return true;
      },
      companyName: null,
      companyWebsite: null,
      backOfficeRootUrl: null,
      email: null,
      setEmail: (email: string) => set({ email }),
      availableCustomers: [],
      roles: [],
      hints: [],
      currentCustomer: null,
      paymentSettings: null,
      setPaymentSettings: (paymentSettings: PaymentSettings | null) => {
        set({ paymentSettings: paymentSettings });
      },
      clearState: () => {
        Sentry.setTag("CompanyId", null);
        Sentry.setTag("CustomerId", null);
        Sentry.setUser(null);
        set({ isAuthenticated: false, availableCustomers: [], currentCustomer: null });
      },
      refreshContext: async (customerId: string | null = null) => {
        if (get().isAuthenticated) {
          if (!customerId) {
            customerId = get().currentCustomer?.id ?? null;
          }
          const customerData = await refreshContext({ customerId });
          get().setCustomerData(customerData);
        } else {
          const companyInfo = await useCompanyStore.getState().getCompanyInfo();
          set({ companyName: companyInfo.name, companyWebsite: companyInfo.website });
        }
      },
    }),
    { name: 'skimmerUserStore' },
  ),
);

export const useSessionStore = createSelectors(useSessionStoreBase);
